// WHAT: Settings view component for configuring Yazio nutrition integration credentials.
// WHY:  Provides users with a secure interface to manage their Yazio username and password for automatic nutrition data synchronization.
// DOMAIN: Settings
// DEPENDENCIES: React, @/components/ui, lucide-react
// RISKS:
//   - WARN: Credentials are stored in Tauri secure storage; backend commands are critical.
//   - NOTE: Password input is masked; credentials are never displayed in the frontend.
//   - WARN: Connection test validates credentials; failures indicate invalid credentials or network issues.

import { Apple, Info, Key, Shield } from "lucide-react";

import { Card, Heading, Stack, StatusIndicator } from "@/components/ui";

import {
  OAuthClientCredentialsForm,
  OAuthClientCredentialsStatus,
  StatusMessages,
  YazioCredentialsForm,
  YazioCredentialsStatus,
} from "./yazio/components";
import { useYazioSettings } from "./yazio/hooks";

/**
 * WHAT:
 *   Settings view component for configuring Yazio nutrition integration credentials.
 *
 * WHY:
 *   Provides users with a secure interface to manage their Yazio username and password for automatic nutrition data synchronization.
 *
 * BEHAVIOR:
 *   - Loads current Yazio settings on mount via `get_yazio_settings` Tauri command.
 *   - Provides form for entering/updating username and password.
 *   - Supports saving, editing, removing credentials, and testing connection.
 *   - Displays configuration status and security information.
 *
 * RISKS:
 *   - WARN: Credentials are stored in Tauri secure storage; backend commands are critical.
 *   - NOTE: Password input is masked; credentials are never displayed in the frontend.
 *   - WARN: Connection test validates credentials; failures indicate invalid credentials or network issues.
 *
 * @returns {JSX.Element} Yazio settings view component
 *
 */
export function YazioSettingsView() {
  const {
    clientId,
    clientSecret,
    error,
    handleCancel,
    handleCancelClientCredentials,
    handleEdit,
    handleEditClientCredentials,
    handleRemove,
    handleRemoveClientCredentials,
    handleSave,
    handleSaveClientCredentials,
    handleTestConnection,
    isConfigured,
    isEditing,
    isEditingClientCredentials,
    loading,
    password,
    saving,
    setClientId,
    setClientSecret,
    setPassword,
    settings,
    setUsername,
    success,
    testing,
    testResult,
    username,
  } = useYazioSettings();

  // Reason: Extract nested ternary to separate variable for better readability
  const credentialsStatusProps = testResult === undefined ? {} : { testResult };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="bg-slate-800/30 border-slate-700/50" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={4}>
          <div className="p-3 bg-tracq-bright-blue/10 rounded-lg">
            <Apple className="w-6 h-6 text-tracq-bright-blue" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading className="text-xl font-semibold text-white" level={2}>
              Yazio Integration
            </Heading>
            <p className="text-slate-300">
              Configure your Yazio credentials to sync nutrition data and track your dietary habits.
            </p>
          </Stack>
        </Stack>
      </Card>

      {/* Status Indicators */}
      <div className="space-y-4">
        <Heading className="text-lg font-medium text-white flex items-center gap-2" level={3}>
          <Key className="w-5 h-5 text-slate-400" />
          Configuration Status
        </Heading>

        <StatusIndicator
          description="Your Yazio username and password for nutrition data synchronization"
          isConfigured={isConfigured}
          label="Yazio Credentials"
          showLock
          size="md"
        />

        <StatusIndicator
          description="OAuth Client ID and Secret for API authentication (required for API access)"
          isConfigured={settings?.hasClientCredentials ?? false}
          label="OAuth Client Credentials"
          showLock
          size="md"
        />
      </div>

      {/* Information Card */}
      <Card className="bg-blue-500/10 border-blue-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-400 mb-1">Yazio Nutrition Integration</h3>
            <p className="text-sm text-blue-300">
              Connect your Yazio account to automatically sync nutrition data, meal tracking, and
              dietary insights. Your credentials are stored securely and used for data
              synchronization.
            </p>
          </div>
        </Stack>
      </Card>

      {/* Configuration Form */}
      {!isConfigured || isEditing ? (
        <YazioCredentialsForm
          isConfigured={isConfigured}
          loading={loading}
          onCancel={handleCancel}
          onPasswordChange={setPassword}
          onSave={handleSave}
          onUsernameChange={setUsername}
          password={password}
          saving={saving}
          username={username}
        />
      ) : null}

      {/* Configured State */}
      {isConfigured && !isEditing ? (
        <YazioCredentialsStatus
          onEdit={handleEdit}
          onRemove={handleRemove}
          onTestConnection={handleTestConnection}
          saving={saving}
          testing={testing}
          {...credentialsStatusProps}
        />
      ) : null}

      {/* OAuth Client Credentials Configuration */}
      {!settings?.hasClientCredentials || isEditingClientCredentials ? (
        <OAuthClientCredentialsForm
          clientId={clientId}
          clientSecret={clientSecret}
          hasClientCredentials={settings?.hasClientCredentials ?? false}
          loading={loading}
          onCancel={handleCancelClientCredentials}
          onClientIdChange={setClientId}
          onClientSecretChange={setClientSecret}
          onSave={handleSaveClientCredentials}
          saving={saving}
        />
      ) : null}

      {/* OAuth Client Credentials Configured State */}
      {settings?.hasClientCredentials && !isEditingClientCredentials ? (
        <OAuthClientCredentialsStatus
          onEdit={handleEditClientCredentials}
          onRemove={handleRemoveClientCredentials}
          saving={saving}
        />
      ) : null}

      {/* Status Messages */}
      <StatusMessages
        {...(error === undefined ? {} : { error })}
        loading={loading}
        {...(success === undefined ? {} : { success })}
      />

      {/* Security Notice */}
      <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Shield className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-green-400 mb-1">Security & Privacy</h3>
            <p className="text-sm text-green-300">
              Your Yazio credentials are stored securely in Tauri&apos;s encrypted storage. No
              sensitive data is ever displayed in the frontend, and all API calls are made directly
              from the secure backend.
            </p>
          </div>
        </Stack>
      </Card>
    </div>
  );
}
